import { useState, useEffect, useCallback } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number // rate vs USD calibrated to match checkout + charm pricing
  locale: string
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  AR: { code: 'ARS', symbol: '$', rate: 1445, locale: 'es-AR' },
  CL: { code: 'CLP', symbol: '$', rate: 1144, locale: 'es-CL' },
  MX: { code: 'MXN', symbol: '$', rate: 22.5, locale: 'es-MX' },
  CO: { code: 'COP', symbol: '$', rate: 4325, locale: 'es-CO' },
  PE: { code: 'PEN', symbol: 'S/', rate: 4.52, locale: 'es-PE' },
  UY: { code: 'UYU', symbol: '$', rate: 48.17, locale: 'es-UY' },
  PY: { code: 'PYG', symbol: '₲', rate: 9031, locale: 'es-PY' },
  BO: { code: 'BOB', symbol: 'Bs', rate: 8.3, locale: 'es-BO' },
  CR: { code: 'CRC', symbol: '₡', rate: 614, locale: 'es-CR' },
  GT: { code: 'GTQ', symbol: 'Q', rate: 9.39, locale: 'es-GT' },
  HN: { code: 'HNL', symbol: 'L', rate: 30.1, locale: 'es-HN' },
  NI: { code: 'NIO', symbol: 'C$', rate: 43.95, locale: 'es-NI' },
  DO: { code: 'DOP', symbol: 'RD$', rate: 69.84, locale: 'es-DO' },
  ES: { code: 'EUR', symbol: '€', rate: 1.11, locale: 'es-ES' },
  BR: { code: 'BRL', symbol: 'R$', rate: 6.15, locale: 'pt-BR' },
  // Dollarized countries
  EC: { code: 'USD', symbol: '$', rate: 1, locale: 'es-EC' },
  PA: { code: 'USD', symbol: '$', rate: 1, locale: 'es-PA' },
  SV: { code: 'USD', symbol: '$', rate: 1, locale: 'es-SV' },
  VE: { code: 'USD', symbol: '$', rate: 1, locale: 'es-VE' },
  US: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
}

// Charm pricing: round down to psychologically attractive numbers
function applyCharmPricing(price: number): number {
  if (price >= 100000) {
    return Math.floor(price / 1000) * 1000 - 100
  }
  if (price >= 10000) {
    return Math.floor(price / 1000) * 1000 - 100
  }
  if (price >= 1000) {
    const hundreds = Math.floor(price / 100) * 100
    return hundreds - 10
  }
  if (price >= 100) {
    const tens = Math.floor(price / 10) * 10
    return tens - 1
  }
  if (price >= 10) {
    return Math.floor(price) - 0.01
  }
  return Math.floor(price) + 0.99
}

function countryToFlag(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    .join('')
}

function getCountryFromLocale(): string {
  try {
    const lang = navigator.language || navigator.languages?.[0] || 'en-US'
    const parts = lang.split('-')
    if (parts.length >= 2) {
      return parts[parts.length - 1].toUpperCase()
    }
  } catch {}
  return 'US'
}

const CACHE_KEY = 'mp_visitor_country'

export function useLocalCurrency() {
  const [countryCode, setCountryCode] = useState<string>('US')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (cached) {
      setCountryCode(cached)
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        const cc = data?.country_code || getCountryFromLocale()
        sessionStorage.setItem(CACHE_KEY, cc)
        setCountryCode(cc)
      })
      .catch(() => {
        const fallback = getCountryFromLocale()
        sessionStorage.setItem(CACHE_KEY, fallback)
        setCountryCode(fallback)
      })
      .finally(() => setIsLoading(false))

    return () => controller.abort()
  }, [])

  const info = CURRENCY_MAP[countryCode] || CURRENCY_MAP['US']
  const isUSD = info.code === 'USD'

  const formatPrice = useCallback(
    (usdAmount: number): string => {
      if (isUSD) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(usdAmount)
      }

      const converted = usdAmount * info.rate
      const charmed = applyCharmPricing(converted)
      const fractionDigits = charmed >= 100 ? 0 : 2

      const formatted = new Intl.NumberFormat(info.locale, {
        style: 'decimal',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(charmed)

      return `${info.symbol}${formatted} ${info.code}`
    },
    [info, isUSD],
  )

  const formatPriceCompact = useCallback(
    (usdAmount: number): string => {
      if (isUSD) {
        return `$${usdAmount.toFixed(2)}`
      }

      const converted = usdAmount * info.rate
      const charmed = applyCharmPricing(converted)

      if (charmed >= 1000) {
        const k = charmed / 1000
        return `${info.symbol}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k ${info.code}`
      }

      const fractionDigits = charmed >= 100 ? 0 : 2
      return `${info.symbol}${charmed.toFixed(fractionDigits)} ${info.code}`
    },
    [info, isUSD],
  )

  return {
    formatPrice,
    formatPriceCompact,
    currencyCode: info.code,
    currencySymbol: info.symbol,
    countryCode,
    countryFlag: countryToFlag(countryCode),
    isUSD,
    isLoading,
  }
}
